import { isDefined } from '@sgme/fp';
import type { ColTypeDef, Column, IRowNode, SetFilterValuesFuncParams } from 'ag-grid-community';
import { getTime, parseISO } from 'date-fns';
import { differenceWith, isEqual, isUndefined, omitBy } from 'lodash-es';

import type { ColumnPreferenceState } from '../blotter.model';

export const dateTimeFormatOptions = {
  year: 'numeric' as const,
  month: 'short' as const,
  day: 'numeric' as const,
  hour: 'numeric' as const,
  minute: 'numeric' as const,
  second: 'numeric' as const,
};

export type DateParser = (input?: unknown) => Date | undefined;
export const makeGridDateSortComparatorWithParser =
  (parseInput: DateParser) => (valueA: any, valueB: any, _nodeA: IRowNode<any>, _nodeB: IRowNode<any>, _: boolean) =>
    makeDateSortComparator(parseInput, valueA, valueB);

export const makeDateSortComparator = (parser: DateParser, valueA: any, valueB: any) => {
  const [dateA, dateB] = [parser(valueA), parser(valueB)] as const;
  if (dateA === undefined || dateB === undefined) {
    return 0;
  }
  return getTime(dateA) - getTime(dateB);
};

export const makeDateFilterComparatorWithParser =
  (parseInput: DateParser) => (filterValue: Date, cellValue: string) => {
    const cellDate = parseInput(cellValue);
    if (cellDate === undefined) {
      return 0;
    }
    return getTime(cellDate) - getTime(filterValue);
  };

export const parseDate: DateParser = input => {
  if (input === undefined) {
    return undefined;
  }
  const result = parseISO(input as string);
  result.setHours(0, 0, 0, 0);
  return result;
};
export const parseDateTime: DateParser = input => (input === undefined ? undefined : parseISO(input as string));

export function hasDifferencesIgnoringUndefined<T extends object>(current: readonly T[], next: readonly T[]) {
  const sanitizedCurrent = current.map(c => omitBy(c, isUndefined));
  const sanitizedNext = next.map(n => omitBy(n, isUndefined));
  const result = differenceWith(sanitizedNext, sanitizedCurrent, isEqual);
  return result.length > 0;
}

export const defaultFilterOptions = ['equals', 'notEqual', 'lessThanOrEqual', 'greaterThanOrEqual'];

export function extractCurrentColumnsFilterState(columnsState: readonly ColumnPreferenceState[]) {
  const currentColumnsFilterState = columnsState.filter(isDefined).reduce(
    (acc, x) => {
      acc[x.colId] = x.filterModel;
      return acc;
    },
    {} as Record<string, unknown>,
  );
  return currentColumnsFilterState;
}

export function sanitizedFilterModel(columns: readonly Column[], currentColumnsFilterState: Record<string, unknown>) {
  const result = columns.reduce(
    (acc, col) => {
      const colId = col.getColId();
      const colFilterDef = col.getColDef().filter;
      const colFilterState = currentColumnsFilterState[colId] as any;

      if (!colFilterState || !colFilterDef) {
        return acc;
      }

      if (
        (colFilterDef === 'agMultiColumnFilter' && colFilterState.filterType !== 'multi') ||
        (colFilterState.filterType === 'multi' && colFilterDef !== 'agMultiColumnFilter')
      ) {
        return acc;
      }

      acc[colId] = colFilterState;
      return acc;
    },
    {} as Record<string, any>,
  );
  return result;
}

export function getSetFilterValuesFromRows(params: SetFilterValuesFuncParams) {
  const values = new Set<string>();
  params.api.forEachNode(r => {
    if (r.data) {
      values.add(r.data[params.colDef.field!]);
    }
  });
  params.success([...values]);
}

export const isPageVisible = () => {
  const document = window.document;
  return document ? !document.hidden : true;
};
export const agSetColumnFilter = <T>(
  colDef?: Partial<ColTypeDef<T>>,
  handleValuesUpdate: boolean = false,
): ColTypeDef<T> => {
  if (handleValuesUpdate) {
    return {
      filter: 'agSetColumnFilter',
      filterParams: {
        values: getSetFilterValuesFromRows,
        refreshValuesOnOpen: true,
      },
      ...colDef,
    };
  } else {
    return {
      filter: 'agSetColumnFilter',
      ...colDef,
    };
  }
};
export const agMultiColumnFilter = (handleValuesUpdate: boolean = false) => {
  if (handleValuesUpdate) {
    return {
      filter: 'agMultiColumnFilter',
      filterParams: {
        newRowsAction: 'keep',
        filters: [
          {
            filter: 'agTextColumnFilter',
          },
          agSetColumnFilter(undefined, handleValuesUpdate),
        ],
      },
    };
  } else {
    return {
      filter: 'agMultiColumnFilter',
      filterParams: {
        newRowsAction: 'keep',
      },
    };
  }
};
